import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import ProfessionalNavigation from '../components/professional/ProfessionalNavigation';
import { useUser } from '../contexts/UserContext';
import { useSalon } from '../contexts/SalonContext';
import { useAuth } from '../contexts/AuthContext';

// Pages
import ProfessionalDashboard from '../components/professional/ProfessionalDashboard';
import ProfessionalAgendaPage from '../components/professional/ProfessionalAgendaPage';
import ProfessionalComissoesPage from '../components/professional/ProfessionalComissoesPage';
import ProfessionalAgendamentosPage from '../components/professional/ProfessionalAgendamentosPage';

/**
 * Painel principal para profissionais
 */
const ProfessionalPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { userRoles, selectedSalonId, userSalons, loading: userLoading } = useUser();
  const { salonConfig, loading: salonLoading } = useSalon();
  const { logout } = useAuth();
  const [professionalInfo, setProfessionalInfo] = useState(null);
  const [professionalId, setProfessionalId] = useState(null);
  
  // Debug logs
  console.log("[ProfessionalPanel] Renderizando");
  console.log(`[ProfessionalPanel] Path atual: ${location.pathname}`);
  console.log(`[ProfessionalPanel] selectedSalonId: ${selectedSalonId}`);
  console.log(`[ProfessionalPanel] Usuário: ${currentUser?.email}`);

  // Buscar o ID do profissional a partir do userRoles e selectedSalonId
  useEffect(() => {
    if (userRoles && selectedSalonId && currentUser) {
      // Encontrar o papel do usuário no salão selecionado
      const salonRole = userRoles.salons.find(salon => salon.salonId === selectedSalonId);
      
      if (salonRole && salonRole.role === 'professional' && salonRole.professionalId) {
        setProfessionalId(salonRole.professionalId);
        console.log(`[ProfessionalPanel] ID do profissional encontrado: ${salonRole.professionalId}`);
      } else {
        console.warn('[ProfessionalPanel] Usuário não é um profissional neste salão ou professionalId não definido');
      }
    }
  }, [userRoles, selectedSalonId, currentUser]);

  // Carregar informações do profissional
  useEffect(() => {
    const loadProfessionalInfo = async () => {
      if (!professionalId) {
        setProfessionalInfo({
          name: currentUser?.displayName || 'Profissional',
          avatarUrl: null
        });
        return;
      }
      
      // TODO: Implementar carregamento de dados do profissional a partir do Firestore usando professionalId
      setProfessionalInfo({
        name: currentUser?.displayName || 'Profissional',
        avatarUrl: null,
        professionalId: professionalId
      });
    };

    if (selectedSalonId && currentUser) {
      loadProfessionalInfo();
    }
  }, [selectedSalonId, currentUser, professionalId]);

  // Função para lidar com logout
  const handleLogout = async () => {
    try {
      console.log("[ProfessionalPanel] Iniciando logout");
      await logout();
      navigate('/login');
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    }
  };

  // Verificar se o usuário é um profissional no salão selecionado
  // Definir fora de qualquer estrutura condicional
  const isProfessional = useCallback(() => {
    if (!userRoles || !userRoles.salons) return false;
    
    const salonRole = userRoles.salons.find(salon => salon.salonId === selectedSalonId);
    return salonRole && salonRole.role === 'professional';
  }, [userRoles, selectedSalonId]);

  // Verificar se temos um salão selecionado
  if (!userLoading && !selectedSalonId) {
    console.warn('[ProfessionalPanel] Nenhum salão selecionado, redirecionando para login');
    return <Navigate to="/login" />;
  }

  // Se não for profissional e o carregamento estiver completo, redirecionar
  if (!userLoading && userRoles && !isProfessional()) {
    console.warn('[ProfessionalPanel] Usuário não é um profissional neste salão, redirecionando');
    return <Navigate to="/login" />;
  }

  return (
    <div className="flex h-screen bg-white">
      {/* Barra de navegação */}
      <ProfessionalNavigation 
        salonLogo={salonConfig?.logoUrl}
        professionalInfo={professionalInfo}
        onLogout={handleLogout}
        salonId={selectedSalonId}
      />

      {/* Conteúdo principal */}
      <main className="flex-1 overflow-y-auto md:ml-20 lg:ml-56 pb-16 md:pb-0">
        <div className="px-4 py-4 md:py-6 md:px-6">
          <Routes>
            {/* Rota principal - Dashboard */}
            <Route index element={<ProfessionalDashboard />} />
            
            {/* Minha Agenda */}
            <Route path="agenda" element={<ProfessionalAgendaPage />} />
            
            {/* Agendamentos */}
            <Route path="agendamentos" element={
              <ProfessionalAgendamentosPage />
            } />
            
            {/* Comissões */}
            <Route path="comissoes" element={<ProfessionalComissoesPage />} />
            
            {/* Desempenho */}
            <Route path="desempenho" element={
              <div className="p-4 bg-white rounded-lg shadow">
                <h1 className="text-xl font-medium text-gray-800 mb-2">Desempenho</h1>
                <p className="text-gray-600">Esta funcionalidade está em desenvolvimento.</p>
              </div>
            } />
            
            {/* Perfil */}
            <Route path="perfil" element={
              <div className="p-4 bg-white rounded-lg shadow">
                <h1 className="text-xl font-medium text-gray-800 mb-2">Perfil do Profissional</h1>
                <p className="text-gray-600">Esta funcionalidade está em desenvolvimento.</p>
              </div>
            } />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default ProfessionalPanel; 